import * as React from 'react';

import { ContextStore } from '../store/contextStore';
import { createCaller } from './api';
import type { ClientOptions } from './api';
import type { SetupStep, SetupStepList, SetupStepValues } from './whatsNextTypes';

// What's next's calls (contract: apiv2/dashboard_setup_steps.openapi.yaml, keyed by operationId so
// whatsNextContract.test.js can hold every path and method to the contract). The Onboarding page's
// plain-fetch caller (api.ts createCaller): a 403 or 409 is an answer the page shows, not a sign-out;
// a 401 still signs out.
export const ENDPOINTS = {
    setupSteps: { method: 'GET', path: '/v2/edit/setup_steps/' },
    setupStepSave: { method: 'POST', path: '/v2/edit/setup_steps/{key}/' },
    setupStepUpload: { method: 'POST', path: '/v2/edit/setup_steps/{key}/upload/' },
    setupStepDismiss: { method: 'POST', path: '/v2/edit/setup_steps/{key}/dismiss/' },
} as const;

export function createSetupStepsApi(options: ClientOptions) {
    const call = createCaller(ENDPOINTS, options);
    return {
        list: (signal?: AbortSignal) => call<SetupStepList>('setupSteps', { signal }),
        /** Only the values that changed: a secret left as its mask is not sent. */
        save: (key: string, values: SetupStepValues) => call<SetupStep>('setupStepSave', { path: { key }, json: { values } }),
        upload: (key: string, file: File) => {
            const form = new FormData();
            form.append('file', file);
            return call<SetupStep>('setupStepUpload', { path: { key }, form });
        },
        dismiss: (key: string, dismissed = true) => call<SetupStep>('setupStepDismiss', { path: { key }, json: { dismissed } }),
    };
}

export type SetupStepsApi = ReturnType<typeof createSetupStepsApi>;

/** The What's next API for the signed-in session (identity-stable: the token is read per call, and a
 *  401 signs out and comes back to the page the user was on). */
export function useSetupStepsApi(): SetupStepsApi {
    const { token, logout } = React.useContext(ContextStore) as any;
    const tokenRef = React.useRef(token);
    tokenRef.current = token;
    const logoutRef = React.useRef(logout);
    logoutRef.current = logout;
    return React.useMemo(
        () => createSetupStepsApi({
            getToken: () => tokenRef.current,
            onUnauthorized: () => logoutRef.current?.(window.location.pathname),
        }),
        [],
    );
}
